import React, { useState } from 'react';
import axios from 'axios';
import { Gift, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Grade bands offered to the Super Admin when creating a demo order.
// BLOCKER-A fix (2026-02-19) — extended with the seven-band commercial model
// (K / G1 / G2 / G3-4 / G5-6 / G7-8 / G9-12). Legacy bands retained for
// backward compatibility with existing demo orders in the pipeline.
// Canonical band values mirror backend ``_BAND_TO_GRADES`` keys.
const GRADE_BANDS = [
  // Seven-band commercial (preferred for new orders)
  'K', '1', '2', '3-4', '5-6', '7-8', '9-12',
  // Legacy edition bands
  'K-2', 'K-5', '3-5', '6-8', 'K-8', 'K-12',
];
const GRADE_BAND_LABEL = {
  'K':    'K (Kindergarten)',
  '1':    'G1 (Grade 1)',
  '2':    'G2 (Grade 2)',
  '3-4':  'G3-4 (Grades 3–4)',
  '5-6':  'G5-6 (Grades 5–6)',
  '7-8':  'G7-8 (Grades 7–8)',
  '9-12': 'G9-12 (Grades 9–12)',
  'K-2':  'K-2 (legacy)',
  'K-5':  'K-5 (legacy)',
  '3-5':  '3-5 (legacy)',
  '6-8':  '6-8 (legacy)',
  'K-8':  'K-8 (legacy)',
  'K-12': 'K-12 (legacy, full-span)',
};
const DURATIONS = [
  { value: 30,  label: '30 days' },
  { value: 60,  label: '60 days' },
  { value: 90,  label: '90 days' },
  { value: 120, label: '120 days' },
];

const inputStyle = {
  width: '100%', padding: '8px 10px', fontSize: 14,
  border: '1px solid #D1D5DB', background: '#fff',
};

const label = (txt) => ({
  display: 'block', fontSize: 11, fontWeight: 700,
  color: '#374151', letterSpacing: 0.4, textTransform: 'uppercase',
  marginBottom: 4,
});

const CreateDemoForm = ({ onCreated }) => {
  const [form, setForm] = useState({
    prospect_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    sales_rep: '',
    grade_band: '6-8',
    duration_days: 60,
    shipping_line1: '',
    shipping_line2: '',
    shipping_city: '',
    shipping_state: '',
    shipping_zip: '',
    reason: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setOk(null); setBusy(true);
    try {
      const token = localStorage.getItem('mosaic_session_token');
      const shipping = form.shipping_line1 ? {
        line1: form.shipping_line1,
        line2: form.shipping_line2 || null,
        city:  form.shipping_city,
        state: form.shipping_state,
        zip:   form.shipping_zip,
      } : null;
      const payload = {
        prospect_name:    form.prospect_name,
        contact_name:     form.contact_name || null,
        contact_email:    form.contact_email || null,
        contact_phone:    form.contact_phone || null,
        sales_rep:        form.sales_rep || null,
        grade_band:       form.grade_band,
        duration_days:    Number(form.duration_days),
        shipping_address: shipping,
        reason:           form.reason || null,
      };
      const res = await axios.post(`${API}/api/usb-orders/demo`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOk(res.data);
      // Soft reset on success — keep rep + grade band for serial demo creation.
      setForm((f) => ({
        ...f,
        prospect_name: '', contact_name: '', contact_email: '', contact_phone: '',
        shipping_line1: '', shipping_line2: '', shipping_city: '',
        shipping_state: '', shipping_zip: '', reason: '',
      }));
      onCreated?.(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === 'string' ? detail : (e.message || 'Could not create demo'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="create-demo-form" style={{ maxWidth: 760 }}>
      <div style={{
        background: '#FEF3C7', border: '1px solid #FDE68A',
        padding: 10, marginBottom: 16, fontSize: 12, color: '#92400E',
      }}>
        <strong>Create a free demo USB.</strong> No PO, no payment, no revenue is recorded. The record will carry
        <code style={{ margin: '0 4px' }}>fulfillment_type=&quot;demo&quot;</code>
        and a synthetic <code style={{ margin: '0 4px' }}>MOS-DEMO-&lt;band&gt;</code> SKU priced at $0.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div>
          <label style={label()}>Prospect (school / district / individual)</label>
          <input data-testid="demo-prospect-name" required style={inputStyle}
                 value={form.prospect_name} onChange={(e) => upd('prospect_name', e.target.value)} />
        </div>
        <div>
          <label style={label()}>Sales Representative</label>
          <input data-testid="demo-sales-rep" style={inputStyle}
                 value={form.sales_rep} onChange={(e) => upd('sales_rep', e.target.value)}
                 placeholder="e.g. Cesar Roca" />
        </div>
        <div>
          <label style={label()}>Contact name</label>
          <input data-testid="demo-contact-name" style={inputStyle}
                 value={form.contact_name} onChange={(e) => upd('contact_name', e.target.value)} />
        </div>
        <div>
          <label style={label()}>Contact email</label>
          <input data-testid="demo-contact-email" type="email" style={inputStyle}
                 value={form.contact_email} onChange={(e) => upd('contact_email', e.target.value)} />
        </div>
        <div>
          <label style={label()}>Contact phone</label>
          <input data-testid="demo-contact-phone" style={inputStyle}
                 value={form.contact_phone} onChange={(e) => upd('contact_phone', e.target.value)} />
        </div>
        <div>
          <label style={label()}>Grade band</label>
          <select data-testid="demo-grade-band" style={inputStyle}
                  value={form.grade_band} onChange={(e) => upd('grade_band', e.target.value)}>
            {GRADE_BANDS.map((b) => <option key={b} value={b}>{GRADE_BAND_LABEL[b] || b}</option>)}
          </select>
        </div>
        <div>
          <label style={label()}>Demo duration</label>
          <select data-testid="demo-duration" style={inputStyle}
                  value={form.duration_days} onChange={(e) => upd('duration_days', e.target.value)}>
            {DURATIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        <label style={label()}>Shipping address (optional at creation; required before Prepare)</label>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginBottom: 10 }}>
          <input data-testid="demo-ship-line1" style={inputStyle} placeholder="Street line 1"
                 value={form.shipping_line1} onChange={(e) => upd('shipping_line1', e.target.value)} />
          <input data-testid="demo-ship-line2" style={inputStyle} placeholder="Line 2 (optional)"
                 value={form.shipping_line2} onChange={(e) => upd('shipping_line2', e.target.value)} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10 }}>
          <input data-testid="demo-ship-city" style={inputStyle} placeholder="City"
                 value={form.shipping_city} onChange={(e) => upd('shipping_city', e.target.value)} />
          <input data-testid="demo-ship-state" style={inputStyle} placeholder="State"
                 value={form.shipping_state} onChange={(e) => upd('shipping_state', e.target.value)} />
          <input data-testid="demo-ship-zip" style={inputStyle} placeholder="ZIP"
                 value={form.shipping_zip} onChange={(e) => upd('shipping_zip', e.target.value)} />
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        <label style={label()}>Internal note / reason (optional)</label>
        <input data-testid="demo-reason" style={inputStyle}
               value={form.reason} onChange={(e) => upd('reason', e.target.value)}
               placeholder="principal presentation · ENL dept evaluation · conference · pilot discussion" />
      </div>

      {err && (
        <div data-testid="demo-error" style={{ marginTop: 14, color: '#991B1B', fontSize: 13 }}>{err}</div>
      )}
      {ok && (
        <div data-testid="demo-created" style={{
          marginTop: 14, color: '#166534', background: '#DCFCE7',
          border: '1px solid #86EFAC', padding: 10, fontSize: 13,
        }}>
          Demo record created · <code>{ok.order_id}</code> · status <code>{ok.status}</code> · no payment or PO was captured.
        </div>
      )}

      <div style={{ marginTop: 20 }}>
        <button type="submit" disabled={busy || !form.prospect_name}
                data-testid="demo-submit"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '10px 20px', fontSize: 14, fontWeight: 700,
                  background: busy ? '#9CA3AF' : '#002FA7',
                  color: '#fff', border: 'none',
                  cursor: busy ? 'not-allowed' : 'pointer',
                }}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Gift size={16} />}
          Prepare Demo USB
        </button>
      </div>
    </form>
  );
};

export default CreateDemoForm;
